<template>
  <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden relative group">
    <!-- Decorative gradient overlay -->
    <div 
      v-if="gradientColor"
      :class="`absolute top-0 right-0 w-64 h-64 ${gradientColor} rounded-full blur-3xl -translate-y-1/2 translate-x-1/2`"
    ></div>
    
    <div class="flex items-center justify-between mb-6 relative z-10">
      <div>
        <h3 class="text-xl font-bold text-white mb-1">{{ title }}</h3>
        <p class="text-gray-400 text-xs font-medium">{{ subtitle }}</p>
      </div>
      <div 
        :class="`w-12 h-12 ${iconBgClass} rounded-xl flex items-center justify-center shadow-lg ${iconBorderClass}`"
      >
        <slot name="icon">
          <svg class="w-6 h-6" :class="iconColorClass" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
        </slot>
      </div>
    </div>
    
    <div class="space-y-2.5 relative z-10">
      <div
        v-for="(account, index) in accounts"
        :key="`${keyPrefix}-${account.formId}`"
        class="group/item flex items-center justify-between p-4 bg-gradient-to-r from-black/40 via-black/30 to-black/40 rounded-xl hover:from-black/60 hover:via-black/50 hover:to-black/60 transition-all duration-300 border border-white/5 hover:border-white/10 hover:shadow-lg hover:scale-[1.02] backdrop-blur-sm"
      >
        <div class="flex items-center gap-4 flex-1 min-w-0">
          <!-- Ranking badge -->
          <div 
            :class="[
              'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg transition-transform duration-300 group-hover/item:scale-110',
              getRankingBadgeClass(index)
            ]"
          >
            {{ index + 1 }}
          </div>
          <div class="flex-1 min-w-0">
            <p 
              :class="`text-white font-semibold text-sm truncate mb-0.5 group-hover/item:${hoverTextColor} transition-colors`"
            >
              {{ account.accountName || account.formTitle || `Form ${account.formId}` }}
            </p>
            <div class="flex items-center gap-2">
              <p class="text-gray-400 text-xs">ID: {{ account.formId }}</p>
              <span v-if="showPercentage" class="text-gray-600">•</span>
              <p v-if="showPercentage" class="text-gray-500 text-xs">
                {{ getPercentageOfTotal(getAccountValue(account), getTopValue()) }}% of #1
              </p>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0 ml-4 text-right">
          <p 
            :class="`text-white font-bold text-xl mb-0.5 group-hover/item:${hoverTextColor} transition-colors`"
          >
            {{ formatNumber(getAccountValue(account)) }}
          </p>
          <p class="text-gray-400 text-xs font-medium">{{ valueLabel }}</p>
        </div>
      </div>
      
      <!-- Empty State -->
      <div v-if="accounts.length === 0" class="text-center py-12 text-gray-400 text-sm">
        <svg class="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
        No account data available
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AccountListCard',
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    accounts: {
      type: Array,
      default: () => []
    },
    valueKey: {
      type: String,
      default: 'totalSubmissions' // 'totalSubmissions' or 'last30DaysSubmissions'
    },
    valueLabel: {
      type: String,
      default: 'submissions'
    },
    gradientColor: {
      type: String,
      default: null
    },
    iconBgClass: {
      type: String,
      default: 'bg-gradient-to-br from-blue-500/30 to-blue-600/30'
    },
    iconBorderClass: {
      type: String,
      default: 'border border-blue-500/20'
    },
    iconColorClass: {
      type: String,
      default: 'text-blue-400'
    },
    hoverTextColor: {
      type: String,
      default: 'text-blue-200'
    },
    keyPrefix: {
      type: String,
      default: 'account'
    },
    showPercentage: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    getAccountValue(account) {
      return account[this.valueKey] || 0;
    },
    getTopValue() {
      if (this.accounts.length === 0) return 1;
      return this.getAccountValue(this.accounts[0]);
    },
    getPercentageOfTotal(value, maxValue) {
      if (!maxValue || maxValue === 0 || !value) return 0;
      return Math.round((value / maxValue) * 100);
    },
    formatNumber(num) {
      return new Intl.NumberFormat('en-US').format(num);
    },
    getRankingBadgeClass(index) {
      if (index === 0) {
        return 'bg-gradient-to-br from-yellow-500/40 to-yellow-600/40 border-2 border-yellow-400/50';
      } else if (index === 1) {
        return 'bg-gradient-to-br from-gray-300/40 to-gray-400/40 border-2 border-gray-300/50';
      } else if (index === 2) {
        return 'bg-gradient-to-br from-orange-600/40 to-orange-700/40 border-2 border-orange-500/50';
      } else {
        return 'bg-gradient-to-br from-blue-500/30 to-blue-600/30 border border-blue-400/30';
      }
    }
  }
};
</script>

